import * as THREE from 'three';
import { FlyControls } from 'three/examples/jsm/controls/FlyControls.js';
import { SETTINGS } from '../settings';
import { StateManager } from '../states/statemanager';
import { debugBoundingBoxes, setDebugBoundingBoxes } from '../constants';

/**
 * A class used to provide various functionalities globally accross the
 * entire application, regardless of current state.
 *
 * @private
 */
class _DebugHelper {
    /**
     * Initializes the helper and registeres all the functions.
     */
    init() {
        debugGui.addButton("Debug Camera", this.toggleDebugCamera.bind(this));
        debugGui.addButton("Log Camera", this.logCamera.bind(this));
        debugGui.addButton("Log Scene", this.logScene.bind(this));
        debugGui.addButton(
            "Bounding Boxes",
            this.toggleBoundingBoxes.bind(this)
        );
        debugGui.addButton("Show Chunk Borders", this.toggleChunkBorders.bind(this));
        debugGui.addButton("Switch State", this.switchState.bind(this));

        if (config.debug) {
            this.toggleBoundingBoxes();
            this.toggleDebugCamera();
        }
    }

    /**
     * Logs the current camera information to the console.
     */
    logCamera() {
        const worldPos = camera.localToWorld(new THREE.Vector3(0, 0, 0));
        console.log("Camera Location:", worldPos.x, worldPos.y, worldPos.z);
    }

    /**
     * Logs a breakdown of the current scene to the console.
     */
    logScene() {
        function _printThis(obj: any) {
            let out = "";
            out += "OBJ";
            out += "[" + obj.name + "]";
            out +=
                " @ " +
                obj.position.x +
                "," +
                obj.position.y +
                "," +
                obj.position.z;

            console.groupCollapsed(out);
            for (let j = 0; j < obj.children.length; ++j) {
                _printThis(obj.children[j]);
            }
            console.groupEnd();
        }
        _printThis(scene);
    }

    toggleDebugCamera() {
        if (!debugCamera) {
            debugCamera = new THREE.PerspectiveCamera(
                45,
                window.innerWidth / window.innerHeight,
                0.1,
                1000
            );
            debugCamera.position.setFromMatrixPosition(camera.matrixWorld);
            debugCamera.quaternion.setFromRotationMatrix(camera.matrixWorld);
            debugControls = new FlyControls(debugCamera, renderer.domElement);
            debugControls.movementSpeed = SETTINGS.DEBUG_MOVEMENT_SPEED;
            debugControls.rollSpeed = Math.PI / 6; // Faster rotation
            debugCamFrust.camera = camera;
            scene.add(debugCamFrust);
        } else {
            debugCamera = null;
            debugControls = null;
            scene.remove(debugCamFrust);
        }
    }

    toggleBoundingBoxes() {
        setDebugBoundingBoxes(!debugBoundingBoxes);

        for (let i = 0; i < GZM.colObjects.length; ++i) {
            GZM.colObjects[i].visible = debugBoundingBoxes;
        }
    }

    switchState() {
        const stateName = prompt("Enter state name:");
        if (stateName) {
            StateManager.prepareAndSwitch(stateName);
        }
    }

    toggleChunkBorders() {
        // Check if chunk borders already exist
        const existingBorders = scene.getObjectByName('ChunkBorders');
        if (existingBorders) {
            scene.remove(existingBorders);
            return;
        }

        // Create container for all chunk borders
        const bordersGroup = new THREE.Group();
        bordersGroup.name = 'ChunkBorders';

        // Get all terrain chunks from GZM
        if (GZM && GZM.terChunks) {
            console.log('Found', GZM.terChunks.length, 'terrain chunks');
            for (let i = 0; i < GZM.terChunks.length; i++) {
                const chunk = GZM.terChunks[i];
                if (chunk && chunk.geometry) {
                    // Get bounding box in world space
                    if (!chunk.geometry.boundingBox) {
                        chunk.geometry.computeBoundingBox();
                    }
                    const box = chunk.geometry.boundingBox.clone();

                    // Transform bounding box to world space
                    box.applyMatrix4(chunk.matrixWorld);

                    console.log('Chunk', i, 'box:', box.min.x, box.min.y, 'to', box.max.x, box.max.y);

                    // Create line geometry for the border - just a flat rectangle at average height
                    const points = [];
                    const z = (box.min.z + box.max.z) / 2 + 5; // Slightly above terrain

                    // Rectangle around chunk
                    points.push(new THREE.Vector3(box.min.x, box.min.y, z));
                    points.push(new THREE.Vector3(box.max.x, box.min.y, z));
                    points.push(new THREE.Vector3(box.max.x, box.max.y, z));
                    points.push(new THREE.Vector3(box.min.x, box.max.y, z));
                    points.push(new THREE.Vector3(box.min.x, box.min.y, z));

                    const geometry = new THREE.BufferGeometry().setFromPoints(points);
                    const material = new THREE.LineBasicMaterial({
                        color: 0xff0000,
                        depthTest: false,
                        linewidth: 3
                    });
                    const line = new THREE.Line(geometry, material);
                    line.renderOrder = 999;
                    bordersGroup.add(line);
                }
            }
        } else {
            console.log('GZM or terChunks not available');
        }

        scene.add(bordersGroup);
        console.log('Added chunk borders to scene');
    }
}

/**
 * The global instance of the DebugHelper.
 *
 * @global
 * @type {_DebugHelper}
 */
export const DebugHelper = new _DebugHelper();
